import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileText, AlertCircle, Shield, ChevronDown, ChevronUp, ExternalLink, Search, Loader2, Globe, Hash, Link } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

const EmailAnalyzer = () => {
  const [activeTab, setActiveTab] = useState('file');
  const [file, setFile] = useState(null);
  const [rawHeader, setRawHeader] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [checkingReputation, setCheckingReputation] = useState(false);
  const [results, setResults] = useState(null);
  const [iocReputation, setIocReputation] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    warnings: true,
    headers: false,
    hops: true,
    attachments: true,
    urls: true,
    iocReputation: true,
    hashes: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.eml')) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a .eml file');
    }
  };

  // Extract IOCs from email analysis results
  const extractIOCsFromResults = (results) => {
    const iocs = [];
    
    // Extract IPs from hops
    if (results.hops) {
      results.hops.forEach(hop => {
        const ipMatch = hop.from?.match(/\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g);
        if (ipMatch) {
          ipMatch.forEach(ip => {
            if (!iocs.find(i => i.value === ip)) {
              iocs.push({ value: ip, type: 'ip', source: 'Email Hop' });
            }
          });
        }
      });
    }
    
    // Extract domains from URLs
    if (results.urls) {
      results.urls.forEach(url => {
        try {
          const urlObj = new URL(url);
          const domain = urlObj.hostname;
          if (!iocs.find(i => i.value === domain)) {
            iocs.push({ value: domain, type: 'domain', source: 'Email Body URL' });
          }
        } catch {}
      });
    }
    
    // Extract hashes from attachments
    if (results.attachments) {
      results.attachments.forEach(att => {
        if (att.md5 && !iocs.find(i => i.value === att.md5)) {
          iocs.push({ value: att.md5, type: 'hash', source: `Attachment: ${att.filename}` });
        }
        if (att.sha256 && !iocs.find(i => i.value === att.sha256)) {
          iocs.push({ value: att.sha256, type: 'hash', source: `Attachment: ${att.filename}` });
        }
      });
    }
    
    return iocs;
  };

  // Check reputation of extracted IOCs
  const checkIOCReputation = async (iocs) => {
    if (iocs.length === 0) {
      toast.info('No IOCs found to check');
      return;
    }
    
    setCheckingReputation(true);
    try {
      const response = await axios.post(`${OSINT_BACKEND}/api/osint/ioc/bulk-check`, {
        iocs: iocs.map(i => i.value)
      });
      
      // Merge reputation data with IOC data
      const reputationResults = iocs.map(ioc => {
        const rep = response.data.results.find(r => r.ioc === ioc.value) || {};
        return {
          ...ioc,
          risk: rep.risk || 'unknown',
          color: rep.color || 'gray',
          details: rep.details || {}
        };
      });
      
      setIocReputation(reputationResults);
      toast.success(`Checked reputation for ${iocs.length} IOCs`);
    } catch (error) {
      console.error('Reputation check error:', error);
      toast.error('Failed to check IOC reputation');
    } finally {
      setCheckingReputation(false);
    }
  };

  const handleAnalyzeFile = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setAnalyzing(true);
    setIocReputation(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${OSINT_BACKEND}/api/osint/mailanalyzer/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      toast.success('Email analyzed successfully');
      
      // Auto-check IOC reputation
      const iocs = extractIOCsFromResults(response.data);
      if (iocs.length > 0) {
        await checkIOCReputation(iocs);
      }
    } catch (error) {
      console.error('Email analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze email');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeHeader = async () => {
    if (!rawHeader.trim()) {
      toast.error('Please paste email headers');
      return;
    }

    setAnalyzing(true);
    setIocReputation(null);
    try {
      const blob = new Blob([rawHeader], { type: 'message/rfc822' });
      const formData = new FormData();
      formData.append('file', blob, 'header.eml');

      const response = await axios.post(`${OSINT_BACKEND}/api/osint/mailanalyzer/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      toast.success('Headers analyzed successfully');
      
      // Auto-check IOC reputation
      const iocs = extractIOCsFromResults(response.data);
      if (iocs.length > 0) {
        await checkIOCReputation(iocs);
      }
    } catch (error) {
      console.error('Header analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze headers');
    } finally {
      setAnalyzing(false);
    }
  };

  const getWarningColor = (tlp) => {
    switch (tlp) {
      case 'red': return 'bg-red-100 text-red-800 border-red-200';
      case 'yellow': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'green': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskColor = (color) => {
    switch (color) {
      case 'red': return 'bg-red-100 text-red-800 border-red-300';
      case 'yellow': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'green': return 'bg-green-100 text-green-800 border-green-300';
      case 'blue': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRiskBadge = (risk, color) => {
    const colorClass = {
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      gray: 'bg-gray-500'
    }[color] || 'bg-gray-500';
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${colorClass}`}>
        {risk.toUpperCase()}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ip': return <Globe className="w-4 h-4" />;
      case 'domain': return <Globe className="w-4 h-4" />;
      case 'hash': return <Hash className="w-4 h-4" />;
      case 'url': return <Link className="w-4 h-4" />;
      default: return <Search className="w-4 h-4" />;
    }
  };

  const SectionHeader = ({ title, section, count }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        <span className="font-semibold">{title}</span>
        {count !== undefined && (
          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">{count}</span>
        )}
      </div>
      {expandedSections[section] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-3xl font-bold font-heading mb-2" data-testid="email-analyzer-title">Email Analyzer</h2>
        <p className="text-muted-foreground">Parse .eml files or analyze raw email headers to extract IOCs and detect threats</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="file" data-testid="file-upload-tab">
            <Upload className="w-4 h-4 mr-2" />
            Upload .eml File
          </TabsTrigger>
          <TabsTrigger value="header" data-testid="raw-header-tab">
            <FileText className="w-4 h-4 mr-2" />
            Paste Raw Headers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="file" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Upload Email File</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".eml"
                  onChange={handleFileChange}
                  className="hidden"
                  id="email-file"
                  data-testid="email-file-input"
                />
                <label htmlFor="email-file" className="cursor-pointer">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm font-medium mb-2">
                    {file ? file.name : 'Click to select .eml file'}
                  </p>
                  <p className="text-xs text-muted-foreground">Only .eml email files are supported</p>
                </label>
              </div>
              <Button
                onClick={handleAnalyzeFile}
                disabled={!file || analyzing}
                className="w-full mt-4"
                data-testid="analyze-file-btn"
              >
                {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {analyzing ? 'Analyzing...' : 'Analyze Email'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="header" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Paste Raw Email Headers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">How to get email headers:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Gmail: Open email - Three dots - Show original</li>
                        <li>Outlook: Open email - File - Properties - Internet headers</li>
                        <li>Apple Mail: View - Message - All Headers</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <textarea
                  value={rawHeader}
                  onChange={(e) => setRawHeader(e.target.value)}
                  placeholder="Paste complete email headers here..."
                  className="w-full h-64 p-4 border rounded-lg font-mono text-sm"
                  data-testid="raw-header-input"
                />
                <Button
                  onClick={handleAnalyzeHeader}
                  disabled={!rawHeader.trim() || analyzing}
                  className="w-full"
                  data-testid="analyze-header-btn"
                >
                  {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  {analyzing ? 'Analyzing...' : 'Analyze Headers'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {results && (
        <div className="mt-6 space-y-4" data-testid="analysis-results">
          {/* IOC Reputation Card - NEW */}
          {iocReputation && iocReputation.length > 0 && (
            <Card className="border-2 border-primary/20">
              <SectionHeader title="IOC Reputation Analysis" section="iocReputation" count={iocReputation.length} />
              {expandedSections.iocReputation && (
                <CardContent className="pt-0">
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                    <div className="flex gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        High Risk: {iocReputation.filter(i => i.color === 'red').length}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        Medium: {iocReputation.filter(i => i.color === 'yellow').length}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Low: {iocReputation.filter(i => i.color === 'green').length}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        Pending: {iocReputation.filter(i => i.color === 'blue').length}
                      </span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => checkIOCReputation(extractIOCsFromResults(results))}
                      disabled={checkingReputation}
                    >
                      {checkingReputation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {iocReputation.map((ioc, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${getRiskColor(ioc.color)}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(ioc.type)}
                            <span className="font-mono text-sm">{ioc.value}</span>
                          </div>
                          {getRiskBadge(ioc.risk, ioc.color)}
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Type: {ioc.type.toUpperCase()}</span>
                          <span>Source: {ioc.source}</span>
                          {ioc.details?.country && <span>Country: {ioc.details.country}</span>}
                          {ioc.details?.is_vpn && <span className="text-yellow-600">VPN</span>}
                          {ioc.details?.is_tor && <span className="text-red-600">Tor</span>}
                          {ioc.details?.is_proxy && <span className="text-yellow-600">Proxy</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Basic Email Information */}
          {results.basic_info && (
            <Card>
              <SectionHeader title="Email Information" section="basicInfo" />
              {expandedSections.basicInfo && (
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { label: 'From', value: results.basic_info.from },
                      { label: 'To', value: results.basic_info.to },
                      { label: 'Subject', value: results.basic_info.subject },
                      { label: 'Date', value: results.basic_info.date },
                      { label: 'Return-Path', value: results.basic_info['return-path'] },
                      { label: 'Message-ID', value: results.basic_info['message-id'] },
                    ].map((item, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className="text-sm font-medium break-all">{item.value || 'N/A'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Security Warnings */}
          {results.warnings && results.warnings.length > 0 && (
            <Card>
              <SectionHeader title="Security Analysis" section="warnings" count={results.warnings.length} />
              {expandedSections.warnings && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {results.warnings.map((warning, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${getWarningColor(warning.warning_tlp)}`}>
                        <div className="flex items-start gap-3">
                          <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-sm">{warning.warning_title}</p>
                            <p className="text-sm mt-1 opacity-90">{warning.warning_message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Email Hops */}
          {results.hops && results.hops.length > 0 && (
            <Card>
              <SectionHeader title="Email Route (Hops)" section="hops" count={results.hops.length} />
              {expandedSections.hops && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {results.hops.map((hop, idx) => (
                      <div key={idx} className="p-4 bg-muted/50 rounded-lg border-l-4 border-primary">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                            {hop.number}
                          </span>
                          <span className="text-sm font-medium">{hop.by}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">From: </span>
                            <span className="font-mono">{hop.from || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">With: </span>
                            <span className="font-mono">{hop.with || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Date: </span>
                            <span>{hop.date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Attachments */}
          {results.attachments && results.attachments.length > 0 && (
            <Card>
              <SectionHeader title="Attachments" section="attachments" count={results.attachments.length} />
              {expandedSections.attachments && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {results.attachments.map((attachment, idx) => (
                      <div key={idx} className="p-4 bg-muted/50 rounded-lg">
                        <p className="font-semibold text-sm mb-2">{attachment.filename}</p>
                        <div className="grid grid-cols-1 gap-2 text-xs font-mono">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">MD5:</span>
                            <span className="break-all">{attachment.md5}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">SHA1:</span>
                            <span className="break-all">{attachment.sha1}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">SHA256:</span>
                            <span className="break-all">{attachment.sha256}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Extracted URLs */}
          {results.urls && results.urls.length > 0 && (
            <Card>
              <SectionHeader title="Extracted URLs" section="urls" count={results.urls.length} />
              {expandedSections.urls && (
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {results.urls.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm font-mono break-all flex-1">{url}</span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-primary hover:text-primary/80"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Email Hashes */}
          {results.eml_hashes && (
            <Card>
              <SectionHeader title="Email File Hashes" section="hashes" />
              {expandedSections.hashes && (
                <CardContent className="pt-0">
                  <div className="space-y-2 font-mono text-sm">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <span className="text-muted-foreground">MD5: </span>
                      <span className="break-all">{results.eml_hashes.md5}</span>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <span className="text-muted-foreground">SHA1: </span>
                      <span className="break-all">{results.eml_hashes.sha1}</span>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <span className="text-muted-foreground">SHA256: </span>
                      <span className="break-all">{results.eml_hashes.sha256}</span>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* All Headers */}
          {results.headers && results.headers.length > 0 && (
            <Card>
              <SectionHeader title="All Headers" section="headers" count={results.headers.length} />
              {expandedSections.headers && (
                <CardContent className="pt-0">
                  <div className="max-h-96 overflow-auto">
                    <div className="space-y-2 font-mono text-xs">
                      {results.headers.map((header, idx) => {
                        const [key, value] = Object.entries(header)[0];
                        return (
                          <div key={idx} className="p-2 bg-muted/50 rounded">
                            <span className="text-primary font-semibold">{key}: </span>
                            <span className="break-all">{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailAnalyzer;
